import { useContext, useLayoutEffect } from "react";

import { Grid } from "@mui/material";
import { CountryNames } from "@simplyk/common";

import { FrontendFormContext } from "../../../../../../../common/contexts/FrontendFormContext";
import { isNameValid, isRequired } from "../../../../../../../common/helpers/validators";
import { useFormV2Context } from "../../../../contexts/FormV2Context";
import { useTrackPrefillEdited } from "../../../../helpers/useTrackPrefillEdited";

import { COUNTRY_NAME_WITH_SHORT_CODE_OPTIONS } from "@/common/constants/countryOptions";
import { useCountryRegion } from "@/common/hooks/useCountryRegion";
import { useDefaultCountryRegion } from "@/common/hooks/useDefaultCountryRegion";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { FormSearchableSelectField } from "@/forms-design-system/FormSearchableSelectField";
import { FormTextField } from "@/forms-design-system/FormTextField";

export const BuyerInformationAddress = () => {
  const { t } = useTranslate();
  const { isPreview } = usePreviewContext();
  const { form } = useFormV2Context("common");
  const { watch, control } = form;
  const { organization } = useContext(FrontendFormContext);
  const { trackNameEdited } = useTrackPrefillEdited();
  const { defaultCountry, defaultRegion } = useDefaultCountryRegion(organization);

  useLayoutEffect(() => {
    if (!form.getValues("country")) {
      form.setValue("country", defaultCountry);
    }
    if (!form.getValues("region") && form.getValues("country") === defaultCountry) {
      form.setValue("region", defaultRegion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countryValue = watch("country");
  const displayStateSelect = countryValue === CountryNames.Canada || countryValue === CountryNames.UnitedStates;
  const { regionOptions } = useCountryRegion(countryValue);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <FormTextField
          control={control}
          name="firstName"
          rules={[isRequired(t("ticketing", "firstNameRequired")), isNameValid(t("common", "invalidCharacterUsed"))]}
          label={t("ticketing", "firstName")}
          data-test="form-firstname"
          disableInjectingGlobalStyles={isPreview}
          error={Boolean(form.formState.errors.firstName?.message)}
          helperText={form.formState.errors.firstName?.message}
          onBlur={trackNameEdited}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          control={control}
          name="lastName"
          rules={[isRequired(t("ticketing", "lastNameRequired")), isNameValid(t("common", "invalidCharacterUsed"))]}
          label={t("ticketing", "lastName")}
          data-test="form-lastname"
          disableInjectingGlobalStyles={isPreview}
          error={Boolean(form.formState.errors.lastName?.message)}
          helperText={form.formState.errors.lastName?.message}
          onBlur={trackNameEdited}
          required
        />
      </Grid>
      {displayStateSelect && (
        <Grid item xs={12} sm={6}>
          <FormSearchableSelectField
            control={control}
            name="region"
            options={regionOptions}
            rules={[isRequired(displayStateSelect, t("common", "isRequired"))]}
            label={countryValue === "Canada" ? t("ticketing", "region") : t("ticketing", "state")}
            data-test="form-region"
            optionDataTest="form-region-option"
            disableInjectingGlobalStyles={isPreview}
            required
            error={Boolean(form.formState.errors.region?.message)}
            defaultValue={defaultRegion}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <FormSearchableSelectField
          control={control}
          name="country"
          options={COUNTRY_NAME_WITH_SHORT_CODE_OPTIONS}
          rules={[isRequired(t("common", "isRequired"))]}
          label={t("ticketing", "country")}
          data-test="form-country"
          optionDataTest="form-country-option"
          defaultValue={defaultCountry}
          disableInjectingGlobalStyles={isPreview}
          required
        />
      </Grid>
    </Grid>
  );
};
